import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from 'lib/supabase';
import AdminDashboardLayout from './components/AdminDashboardLayout';
import KPICard from './components/KPICard';
import MonthlyRegistrationsChart from './components/MonthlyRegistrationsChart';
import RoleComparisonChart from './components/RoleComparisonChart';

// Create context for dashboard refresh
export const DashboardRefreshContext = createContext(null);

const AdminDashboardOverview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState({
    totalClienti: 0,
    totalProfessionisti: 0,
    verifiedProfessionisti: 0,
    pendingProfessionisti: 0,
    activeRequests: 0,
    completedRequestsThisMonth: 0,
    quotesLast7Days: 0,
    quotesThisMonth: 0
  });
  const [monthlyData, setMonthlyData] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch KPI data
      const { data: users, error: usersError } = await supabase
        ?.from('users')
        ?.select('role, verification_status, created_at');

      if (usersError) throw usersError;

      // Calculate KPIs
      const clienti = users?.filter(u => u?.role === 'cliente') || [];
      const professionisti = users?.filter(u => u?.role === 'professionista') || [];
      const verified = professionisti?.filter(p => p?.verification_status === 'approved') || [];
      const pending = professionisti?.filter(p => p?.verification_status === 'pending') || [];

      // Fetch active/pending service requests
      const { count: activeRequestsCount } = await supabase
        ?.from('service_requests')
        ?.select('id', { count: 'exact', head: true })
        ?.in('status', ['active', 'pending']);

      // Fetch completed requests this month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString();
      const { count: completedThisMonth } = await supabase
        ?.from('service_requests')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('status', 'completed')
        ?.gte('updated_at', startOfMonth);

      // Fetch quotes in last 7 days
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString();
      const { count: quotesLast7Days } = await supabase
        ?.from('quotes')
        ?.select('id', { count: 'exact', head: true })
        ?.gte('created_at', sevenDaysAgo);

      // Fetch quotes this month
      const { count: quotesThisMonth } = await supabase
        ?.from('quotes')
        ?.select('id', { count: 'exact', head: true })
        ?.gte('created_at', startOfMonth);

      setKpiData({
        totalClienti: clienti?.length,
        totalProfessionisti: professionisti?.length,
        verifiedProfessionisti: verified?.length,
        pendingProfessionisti: pending?.length,
        activeRequests: activeRequestsCount || 0,
        completedRequestsThisMonth: completedThisMonth || 0,
        quotesLast7Days: quotesLast7Days || 0,
        quotesThisMonth: quotesThisMonth || 0
      });

      // Process monthly registrations data
      const monthlyRegistrations = processMonthlyData(users);
      setMonthlyData(monthlyRegistrations);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const processMonthlyData = (users) => {
    const monthsMap = {};
    const now = new Date();
    
    // Initialize last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now?.getFullYear(), now?.getMonth() - i, 1);
      const key = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      monthsMap[key] = { month: key, total: 0, clienti: 0, professionisti: 0 };
    }

    // Count registrations by month
    users?.forEach(user => {
      const date = new Date(user?.created_at);
      const key = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      
      if (monthsMap?.[key]) {
        monthsMap[key].total++;
        if (user?.role === 'cliente') monthsMap[key].clienti++;
        if (user?.role === 'professionista') monthsMap[key].professionisti++;
      }
    });

    return Object?.values(monthsMap);
  };

  if (loading) {
    return (
      <AdminDashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600">Caricamento dati...</p>
          </div>
        </div>
      </AdminDashboardLayout>
    );
  }

  if (error) {
    return (
      <AdminDashboardLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </AdminDashboardLayout>
    );
  }

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Panoramica</h1>
          <p className="text-gray-600 mt-2">Dashboard amministrativa - Metriche principali</p>
        </div>

        {/* KPI Cards Grid - Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="Clienti Totali"
            value={kpiData?.totalClienti}
            icon="Users"
            color="blue"
            onClick={() => {}}
            subText=""
          />
          <KPICard
            title="Professionisti Totali"
            value={kpiData?.totalProfessionisti}
            icon="Briefcase"
            color="purple"
            onClick={() => {}}
            subText=""
          />
          <KPICard
            title="Professionisti Verificati"
            value={kpiData?.verifiedProfessionisti}
            icon="CheckCircle"
            color="green"
            onClick={() => {}}
            subText=""
          />
          <KPICard
            title="In Attesa Verifica"
            value={kpiData?.pendingProfessionisti}
            icon="Clock"
            color="orange"
            onClick={() => navigate('/admin/professionisti/pending')}
            subText=""
          />
        </div>

        {/* KPI Cards Grid - Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:w-1/2">
          <KPICard
            title="Richieste Attive"
            value={kpiData?.activeRequests}
            icon="FileText"
            color="teal"
            subText={`${kpiData?.completedRequestsThisMonth} completate questo mese`}
          />
          <KPICard
            title="Preventivi inviati (7gg)"
            value={kpiData?.quotesLast7Days}
            icon="Send"
            color="indigo"
            subText={`${kpiData?.quotesThisMonth} totali nel mese`}
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <MonthlyRegistrationsChart data={monthlyData} />
          <RoleComparisonChart 
            clienti={kpiData?.totalClienti} 
            professionisti={kpiData?.totalProfessionisti} 
          />
        </div>
      </div>
    </AdminDashboardLayout>
  );
};

export default AdminDashboardOverview;